/**
 * Slide-over panel.
 *
 * 480px right-edge dialog over a black scrim per Claude Design Files
 * README sections 5.12 and 5.13. The shell mounts this once at the
 * application root; the panel renders content based on the current
 * `useSlideOver` target.
 *
 * M2 shipped the empty mount point. M4 fills in the task variant
 * with the fields, body, and audit log per spec section 15.4. The
 * deal variant fills in during M8.
 */

import * as Dialog from '@radix-ui/react-dialog';
import { XMarkIcon } from '@heroicons/react/24/outline';
import ReactMarkdown from 'react-markdown';

import { useSlideOver } from '@/lib/slide-over';
import { AuditLog } from './AuditLog';
import {
  useTask,
  useDeleteTask,
  useCompleteTask,
  useUpdateTask,
  useFrogTask,
  useCreateTaskSubtask,
  useUpdateTaskSubtask,
  useDeleteTaskSubtask,
} from '@/lib/api/tasks';
import {
  useLead,
  useDisqualifyLead,
  useConvertLead,
  useOpportunity,
  useUpdateLead,
  useUpdateOpportunity,
  useWinOpportunity,
  useLoseOpportunity,
  useRecycleLead,
  useDeleteLead,
  useDeleteOpportunity,
  useCreateLeadSubtask,
  useUpdateLeadSubtask,
  useDeleteLeadSubtask,
  useCreateOpportunitySubtask,
  useUpdateOpportunitySubtask,
  useDeleteOpportunitySubtask,
} from '@/lib/api/sales';
import { useCelebration } from '@/lib/celebration';
import { STATUS_TRANSITIONS } from '@/lib/task-status-transitions';
import { cn, priorityClass } from '@/lib/utils';
import {
  BUDGET_SIGNAL,
  CAPABILITY,
  CUSTOMER_STATUS,
  CUSTOMER_TYPE,
  INDUSTRY,
  INDUSTRY_LABELS,
  LEAD_ACTION_LABELS,
  LEAD_ACTION_SELECTABLE,
  LEAD_SOURCE,
  NEXT_LEAD_ACTION,
  NEXT_OPPORTUNITY_ACTION,
  OPP_TYPE,
  OPPORTUNITY_ACTION_LABELS,
  OPPORTUNITY_ACTION_SELECTABLE,
  TASK_PRIORITY,
  TASK_STATUS,
  TASK_TYPE,
  type BudgetSignal,
  type Capability,
  type CustomerStatus,
  type CustomerType,
  type Industry,
  type LeadAction,
  type LeadSource,
  type OppType,
  type OpportunityAction,
  type TaskPriority,
  type TaskStatus,
  type TaskType,
} from '@workspace/shared';
import { useEffect, useState } from 'react';

export function SlideOver() {
  const target = useSlideOver((s) => s.target);
  const close = useSlideOver((s) => s.close);
  const isOpen = target !== null;

  return (
    <Dialog.Root open={isOpen} onOpenChange={(next) => (next ? null : close())}>
      <Dialog.Portal>
        <Dialog.Overlay
          aria-hidden
          className="fixed inset-0 z-40 bg-black/55 data-[state=open]:animate-in data-[state=open]:fade-in"
        />
        <Dialog.Content
          aria-label="Detail panel"
          className="fixed right-0 top-0 z-50 flex h-full w-[480px] max-w-full flex-col border-l border-mx-grey-800 bg-mx-card text-mx-grey-300"
        >
          {target?.kind === 'task' && <TaskSlideOver taskId={target.taskId} />}
          {target?.kind === 'lead' && <LeadSlideOver leadId={target.leadId} />}
          {target?.kind === 'opportunity' && (
            <OpportunitySlideOver opportunityId={target.opportunityId} />
          )}
          {!target && <PlaceholderBody label="DETAIL" milestone="next milestone" />}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function PlaceholderBody({ label, milestone }: { label: string; milestone: string }) {
  return (
    <>
      <div className="flex items-center justify-between border-b border-mx-grey-800 px-mx-5 py-mx-4">
        <Dialog.Title className="mono-label text-mx-grey-mid">{label}</Dialog.Title>
        <Dialog.Close
          aria-label="Close detail panel"
          className="flex h-7 w-7 items-center justify-center text-mx-grey-mid hover:text-mx-white"
        >
          <XMarkIcon className="h-5 w-5" strokeWidth={1.6} aria-hidden />
        </Dialog.Close>
      </div>
      <Dialog.Description className="sr-only">
        Detail content for the selected record.
      </Dialog.Description>
      <div className="flex-1 overflow-y-auto px-mx-5 py-mx-5 text-mx-body-sm text-mx-grey-mid">
        Detail content lands in {milestone}.
      </div>
    </>
  );
}

/** Return today's date as a UTC ISO date string (YYYY-MM-DD). The
 * frog-state lookup compares against this so the chip flips on the
 * same day Peter pressed the button regardless of his system clock. */
function todayIsoDate(): string {
  const now = new Date();
  const y = now.getUTCFullYear();
  const m = String(now.getUTCMonth() + 1).padStart(2, '0');
  const d = String(now.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Brisbane-formatted date stamp for audit-log rows. The spec calls for
 * the en-AU `DD MMM HH:mm` shape rendered against Australia/Brisbane;
 * we use Intl rather than a date-fns dependency in this leaf path.
 */
function formatBrisbane(iso: string): string {
  const date = new Date(iso);
  const formatter = new Intl.DateTimeFormat('en-AU', {
    timeZone: 'Australia/Brisbane',
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  return formatter.format(date);
}

/**
 * Task slide-over.
 *
 * UAT-25 rebuilt this from a read-only chip strip into the editor
 * Peter actually asked for: editable title, type / status / priority /
 * due-date selects, body textarea, subtask checklist, audit log,
 * delete + complete + frog actions. Every editable field calls the
 * matching PATCH on commit; subtasks call the dedicated CRUD surface.
 */
function TaskSlideOver({ taskId }: { taskId: string }) {
  const close = useSlideOver((s) => s.close);
  const { data: task, isLoading, isError } = useTask(taskId);
  const completeTask = useCompleteTask();
  const deleteTask = useDeleteTask();
  const updateTask = useUpdateTask();
  const frogTask = useFrogTask();
  const createSubtask = useCreateTaskSubtask();
  const updateSubtask = useUpdateTaskSubtask();
  const deleteSubtask = useDeleteTaskSubtask();

  // Local state for the controlled inputs. We seed from the server
  // payload and overwrite on every refetch so optimistic edits land
  // smoothly without the cursor jumping mid-typing.
  const [titleDraft, setTitleDraft] = useState('');
  const [bodyDraft, setBodyDraft] = useState('');
  const [newSubtaskTitle, setNewSubtaskTitle] = useState('');
  const [completedFlash, setCompletedFlash] = useState<string | null>(null);
  const [auditOpen, setAuditOpen] = useState(false);

  useEffect(() => {
    // Reseed the controlled inputs only when the row identity changes
    // (the slide-over opens against a new task) or when the server
    // returns a fresh `updatedAt` (after a save round-trips). Depending
    // on the whole `task` object would reset the drafts on every render
    // because hooks can return fresh references for the same row, which
    // would clobber in-progress edits — and breaks the title-blur test.
    if (task) {
      setTitleDraft(task.title);
      setBodyDraft(task.body ?? '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task?.id, task?.updatedAt]);

  const today = todayIsoDate();
  const isFroggedToday = (task?.frogDates ?? []).includes(today);

  return (
    <>
      <header className="flex items-center justify-between border-b border-mx-grey-800 px-mx-5 py-mx-4">
        <div className="flex items-center gap-mx-2">
          {task && (
            <>
              <span className="mono-label border border-mx-grey-800 bg-mx-grey-900 px-mx-2 py-[2px] text-mx-mono-sm">
                {task.type}
              </span>
              <span className="mono-label border border-mx-grey-800 bg-mx-grey-900 px-mx-2 py-[2px] text-mx-mono-sm">
                {task.status.replace('_', ' ')}
              </span>
              {isFroggedToday && (
                <span className="mono-label bg-mx-orange px-mx-2 py-[2px] text-mx-mono-sm text-mx-white">
                  FROG
                </span>
              )}
            </>
          )}
        </div>
        <Dialog.Close
          aria-label="Close detail panel"
          className="flex h-7 w-7 items-center justify-center text-mx-grey-mid hover:text-mx-white"
        >
          <XMarkIcon className="h-5 w-5" strokeWidth={1.6} aria-hidden />
        </Dialog.Close>
      </header>

      <Dialog.Description className="sr-only">Task detail panel.</Dialog.Description>

      {isLoading && <p className="px-mx-5 py-mx-5 text-mx-body-sm text-mx-grey-mid">Loading…</p>}
      {isError && (
        <p className="px-mx-5 py-mx-5 text-mx-body-sm text-mx-neg">Could not load task.</p>
      )}

      {task && (
        <div className="flex flex-1 flex-col overflow-y-auto">
          <Dialog.Title asChild>
            <h2 className="sr-only">{task.title}</h2>
          </Dialog.Title>

          {/* Editable title — commit on blur so every keystroke does
              not trip a PATCH; explicit blur or Enter signals "done". */}
          <label className="block px-mx-5 pt-mx-4">
            <span className="mono-label text-mx-grey-mid">TITLE</span>
            <input
              aria-label="Task title"
              value={titleDraft}
              onChange={(e) => setTitleDraft(e.target.value)}
              onBlur={() => {
                const next = titleDraft.trim();
                if (next && next !== task.title) {
                  updateTask.mutate({ id: taskId, patch: { title: next } });
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  e.currentTarget.blur();
                }
              }}
              className="mt-mx-1 block w-full border border-mx-grey-800 bg-mx-grey-900 px-mx-3 py-mx-2 font-display text-[20px] font-semibold leading-tight text-mx-grey-300 focus:border-mx-orange focus:outline-none"
            />
          </label>

          {/* Field grid. Each select / input is bound to a one-off
              PATCH so smart-default decisions persist immediately. */}
          <dl className="grid grid-cols-[140px_1fr] items-center gap-y-mx-2 px-mx-5 py-mx-4 text-mx-body-sm">
            <dt className="mono-label text-mx-grey-mid">TYPE</dt>
            <dd>
              <select
                aria-label="Task type"
                value={task.type}
                onChange={(e) =>
                  updateTask.mutate({
                    id: taskId,
                    patch: { type: e.target.value as TaskType },
                  })
                }
                className="h-8 border border-mx-grey-800 bg-mx-grey-900 px-mx-2 text-mx-body-sm text-mx-grey-300 focus:border-mx-orange focus:outline-none"
              >
                {TASK_TYPE.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </dd>

            <dt className="mono-label text-mx-grey-mid">STATUS</dt>
            <dd>
              <select
                aria-label="Task status"
                value={task.status}
                onChange={(e) => {
                  const next = e.target.value as TaskStatus;
                  if (!STATUS_TRANSITIONS[task.status].includes(next)) {
                    // Defensive: should not happen because the option
                    // is disabled, but the browser can still fire a
                    // change event in edge cases.
                    return;
                  }
                  updateTask.mutate({ id: taskId, patch: { status: next } });
                }}
                className="h-8 border border-mx-grey-800 bg-mx-grey-900 px-mx-2 text-mx-body-sm text-mx-grey-300 focus:border-mx-orange focus:outline-none"
              >
                {TASK_STATUS.map((s) => {
                  const allowed = STATUS_TRANSITIONS[task.status].includes(s);
                  return (
                    <option key={s} value={s} disabled={!allowed}>
                      {s.replace('_', ' ')}
                      {!allowed ? ' (blocked)' : ''}
                    </option>
                  );
                })}
              </select>
            </dd>

            <dt className="mono-label text-mx-grey-mid">PRIORITY</dt>
            <dd>
              <select
                aria-label="Task priority"
                value={task.priority}
                onChange={(e) =>
                  updateTask.mutate({
                    id: taskId,
                    patch: { priority: e.target.value as TaskPriority },
                  })
                }
                className={cn(
                  'h-8 border border-mx-grey-800 bg-mx-grey-900 px-mx-2 text-mx-body-sm focus:border-mx-orange focus:outline-none',
                  priorityClass(task.priority),
                )}
              >
                {TASK_PRIORITY.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </dd>

            <dt className="mono-label text-mx-grey-mid">DUE</dt>
            <dd>
              <input
                type="date"
                aria-label="Due date"
                value={task.dueDate ?? ''}
                onChange={(e) =>
                  updateTask.mutate({
                    id: taskId,
                    patch: { dueDate: e.target.value === '' ? null : e.target.value },
                  })
                }
                className="h-8 border border-mx-grey-800 bg-mx-grey-900 px-mx-2 text-mx-body-sm text-mx-grey-300 focus:border-mx-orange focus:outline-none"
              />
            </dd>

            <dt className="mono-label text-mx-grey-mid">SOURCE</dt>
            <dd className="text-mx-grey-300">{task.source}</dd>

            {task.linkedLeadId && (
              <>
                <dt className="mono-label text-mx-grey-mid">LINKED LEAD</dt>
                <dd className="text-mx-grey-300">{task.linkedLeadId}</dd>
              </>
            )}
            {task.linkedOpportunityId && (
              <>
                <dt className="mono-label text-mx-grey-mid">LINKED DEAL</dt>
                <dd className="text-mx-grey-300">{task.linkedOpportunityId}</dd>
              </>
            )}
          </dl>

          {/* Subtasks. Mirrors LeadSubtask / OpportunitySubtask CRUD;
              renders a checklist with inline delete and a footer input
              for new entries. UAT-25 added the parent variant. */}
          <section className="border-t border-mx-grey-800 px-mx-5 py-mx-4">
            <p className="eyebrow mono-label text-mx-grey-mid">
              <span className="num">01</span>
              Subtasks
            </p>
            <ul className="mt-mx-2 flex flex-col gap-mx-1">
              {(task.subtasks ?? []).map((sub) => (
                <li key={sub.id} className="flex items-center gap-mx-2">
                  <input
                    type="checkbox"
                    aria-label={`Subtask: ${sub.title}`}
                    checked={sub.done}
                    onChange={(e) =>
                      updateSubtask.mutate({
                        taskId,
                        subtaskId: sub.id,
                        patch: { done: e.target.checked },
                      })
                    }
                  />
                  <span
                    className={cn(
                      'flex-1 text-mx-body-sm',
                      sub.done ? 'text-mx-grey-mid line-through' : 'text-mx-grey-300',
                    )}
                  >
                    {sub.title}
                  </span>
                  <button
                    type="button"
                    aria-label={`Delete subtask ${sub.title}`}
                    onClick={() => deleteSubtask.mutate({ taskId, subtaskId: sub.id })}
                    className="text-mx-grey-mid hover:text-mx-neg"
                  >
                    <XMarkIcon className="h-4 w-4" strokeWidth={1.6} aria-hidden />
                  </button>
                </li>
              ))}
              {(task.subtasks ?? []).length === 0 && (
                <li className="text-mx-body-sm text-mx-grey-mid">No subtasks yet.</li>
              )}
            </ul>
            <form
              className="mt-mx-3 flex gap-mx-2"
              onSubmit={(e) => {
                e.preventDefault();
                const next = newSubtaskTitle.trim();
                if (!next) return;
                createSubtask.mutate(
                  { taskId, input: { title: next } },
                  {
                    onSuccess: () => {
                      setNewSubtaskTitle('');
                    },
                  },
                );
              }}
            >
              <input
                aria-label="New subtask"
                value={newSubtaskTitle}
                onChange={(e) => setNewSubtaskTitle(e.target.value)}
                placeholder="Add subtask"
                className="h-8 flex-1 border border-mx-grey-800 bg-mx-grey-900 px-mx-2 text-mx-body-sm text-mx-grey-300 focus:border-mx-orange focus:outline-none"
              />
              <button
                type="submit"
                disabled={!newSubtaskTitle.trim() || createSubtask.isPending}
                className="h-8 border border-mx-grey-800 bg-mx-grey-900 px-mx-3 text-mx-mono-sm font-medium text-mx-grey-300 hover:bg-mx-grey-850 disabled:opacity-50"
              >
                ADD
              </button>
            </form>
          </section>

          {/* Body / notes textarea. Commits on blur so a deliberate
              focus-out signals "done" without spamming the API on
              every keystroke. */}
          <section className="border-t border-mx-grey-800 px-mx-5 py-mx-4">
            <p className="eyebrow mono-label text-mx-grey-mid">
              <span className="num">02</span>
              Notes
            </p>
            <textarea
              aria-label="Task notes"
              value={bodyDraft}
              onChange={(e) => setBodyDraft(e.target.value)}
              onBlur={() => {
                const next = bodyDraft;
                if (next !== (task.body ?? '')) {
                  updateTask.mutate({
                    id: taskId,
                    patch: { body: next.length === 0 ? null : next },
                  });
                }
              }}
              rows={5}
              placeholder="Add notes in markdown."
              className="mt-mx-2 block w-full resize-y border border-mx-grey-800 bg-mx-grey-900 px-mx-3 py-mx-2 text-mx-body-sm text-mx-grey-300 focus:border-mx-orange focus:outline-none"
            />
            {task.body && bodyDraft === task.body && (
              <div className="prose prose-sm mt-mx-3 max-w-none text-mx-body-sm text-mx-grey-300">
                <ReactMarkdown
                  components={{ a: ({ href, children }) => <span title={href}>{children}</span> }}
                >
                  {task.body}
                </ReactMarkdown>
              </div>
            )}
          </section>

          {/* Audit log. Collapsed by default to keep the slide-over
              compact; expanded reveals the last 20 events. */}
          <section className="border-t border-mx-grey-800 px-mx-5 py-mx-4">
            <button
              type="button"
              onClick={() => setAuditOpen((v) => !v)}
              aria-expanded={auditOpen}
              className="eyebrow mono-label flex w-full items-center justify-between text-mx-grey-mid"
            >
              <span>
                <span className="num">03</span>
                Activity
              </span>
              <span aria-hidden>{auditOpen ? '−' : '+'}</span>
            </button>
            {auditOpen && (
              <ul className="mt-mx-2 flex flex-col gap-mx-1">
                {(task.auditLog ?? []).slice(0, 20).map((entry) => (
                  <li
                    key={entry.id}
                    className="flex items-center justify-between border-b border-mx-grey-800 py-mx-1 text-mx-body-sm"
                  >
                    <span className="mono-label text-mx-grey-mid">
                      {entry.eventType.replace(/_/g, ' ')}
                    </span>
                    <span className="mono-label tnum text-mx-grey-500">
                      {formatBrisbane(entry.occurredAt)}
                    </span>
                  </li>
                ))}
                {(task.auditLog ?? []).length === 0 && (
                  <li className="text-mx-body-sm text-mx-grey-mid">No activity yet.</li>
                )}
              </ul>
            )}
          </section>

          {completedFlash && (
            <p
              role="status"
              className="border-t border-mx-grey-800 bg-mx-grey-900 px-mx-5 py-mx-2 text-mx-body-sm text-mx-pos"
            >
              {completedFlash}
            </p>
          )}

          {/* Delete is destructive — flagged red, no confirmation
              modal because the slide-over close itself reverts the
              view; archived rows can still be restored via the
              Prisma row. */}
          <footer className="mt-auto flex items-center gap-mx-2 border-t border-mx-grey-800 bg-mx-grey-900 px-mx-5 py-mx-4">
            <button
              type="button"
              onClick={() => {
                deleteTask.mutate(taskId, { onSuccess: close });
              }}
              className="text-mx-mono-sm font-medium text-mx-neg hover:opacity-80"
            >
              DELETE
            </button>
            <button
              type="button"
              onClick={() => frogTask.mutate({ id: taskId, on: !isFroggedToday })}
              className={cn(
                'text-mx-mono-sm font-medium',
                isFroggedToday
                  ? 'text-mx-orange hover:text-mx-orange-hover'
                  : 'text-mx-grey-mid hover:text-mx-white',
              )}
            >
              {isFroggedToday ? 'UNFROG' : 'FROG'}
            </button>
            {!task.completedAt && (
              <button
                type="button"
                onClick={() =>
                  completeTask.mutate(taskId, {
                    onSuccess: (data) => {
                      const message = data.autoPulledTaskId
                        ? 'Task complete. Auto-pulled next.'
                        : 'Task complete.';
                      setCompletedFlash(message);
                      // Hold the slide-over open briefly so the inline
                      // banner reads as feedback, then close.
                      setTimeout(() => {
                        setCompletedFlash(null);
                        close();
                      }, 600);
                    },
                  })
                }
                className="ml-auto flex h-9 items-center bg-mx-orange px-mx-3 text-mx-body-sm font-medium text-mx-white hover:bg-mx-orange-hover"
              >
                Complete
              </button>
            )}
          </footer>
        </div>
      )}
    </>
  );
}

/**
 * Lead slide-over.
 *
 * UAT-34 (PR #34) rebuilt this from a read-only `dl` strip into the
 * full editor surface Peter asked for: every wire field becomes a
 * controlled input or select that PATCHes on blur (text / textarea /
 * number / date) or on change (selects / checkbox). The stage select
 * also handles the DEAD-to-anything path by routing through the
 * recycle endpoint so `disqualifiedAt` clears cleanly. The footer
 * gains a RECYCLE button when the lead is currently DEAD.
 */
function LeadSlideOver({ leadId }: { leadId: string }) {
  const close = useSlideOver((s) => s.close);
  const { data: lead, isLoading, isError } = useLead(leadId);
  const updateLead = useUpdateLead();
  const disqualify = useDisqualifyLead();
  const recycle = useRecycleLead();
  const convert = useConvertLead();
  const deleteLead = useDeleteLead();
  const createSubtask = useCreateLeadSubtask();
  const updateSubtask = useUpdateLeadSubtask();
  const deleteSubtask = useDeleteLeadSubtask();
  const [opportunityName, setOpportunityName] = useState('');
  const [newSubtaskTitle, setNewSubtaskTitle] = useState('');

  // Local controlled-input drafts. Seeded from the wire payload on row
  // identity / updatedAt change so a save round-trip does not clobber
  // an in-progress edit (mirrors the TaskSlideOver pattern from PR #25).
  const [companyDraft, setCompanyDraft] = useState('');
  const [contactNameDraft, setContactNameDraft] = useState('');
  const [contactRoleDraft, setContactRoleDraft] = useState('');
  const [contactEmailDraft, setContactEmailDraft] = useState('');
  const [contactPhoneDraft, setContactPhoneDraft] = useState('');
  const [potentialValueDraft, setPotentialValueDraft] = useState('');
  const [notesDraft, setNotesDraft] = useState('');

  useEffect(() => {
    if (lead) {
      setCompanyDraft(lead.companyName);
      setContactNameDraft(lead.contactName ?? '');
      setContactRoleDraft(lead.contactRole ?? '');
      setContactEmailDraft(lead.contactEmail ?? '');
      setContactPhoneDraft(lead.contactPhone ?? '');
      setPotentialValueDraft(lead.potentialValue ?? '');
      setNotesDraft(lead.notes ?? '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lead?.id, lead?.updatedAt]);

  /**
   * Commit a nullable text-field draft on blur. Empty input becomes
   * `null` so the wire shape matches the Prisma column nullability;
   * no PATCH fires if the trimmed value matches the current server
   * state.
   */
  function commitNullableText(
    field: 'contactName' | 'contactRole' | 'contactEmail' | 'contactPhone' | 'industryVertical',
    draft: string,
  ): void {
    if (!lead) return;
    const trimmed = draft.trim();
    const next = trimmed === '' ? null : trimmed;
    const currentValue = lead[field] ?? null;
    if (next === currentValue) return;
    updateLead.mutate({ id: leadId, patch: { [field]: next } });
  }

  return (
    <>
      <header className="flex items-center justify-between border-b border-mx-grey-800 px-mx-5 py-mx-4">
        <div className="flex items-center gap-mx-2">
          {lead && (
            <>
              <span className="mono-label border border-mx-grey-800 bg-mx-grey-900 px-mx-2 py-[2px] text-mx-mono-sm">
                LEAD
              </span>
              <span className="mono-label border border-mx-grey-800 bg-mx-grey-900 px-mx-2 py-[2px] text-mx-mono-sm">
                {lead.stage}
              </span>
            </>
          )}
        </div>
        <Dialog.Close
          aria-label="Close detail panel"
          className="flex h-7 w-7 items-center justify-center text-mx-grey-mid hover:text-mx-white"
        >
          <XMarkIcon className="h-5 w-5" strokeWidth={1.6} aria-hidden />
        </Dialog.Close>
      </header>

      <Dialog.Description className="sr-only">Lead detail panel.</Dialog.Description>

      {isLoading && <p className="px-mx-5 py-mx-5 text-mx-body-sm text-mx-grey-mid">Loading…</p>}
      {isError && (
        <p className="px-mx-5 py-mx-5 text-mx-body-sm text-mx-neg">Could not load lead.</p>
      )}

      {lead && (
        <div className="flex flex-1 flex-col overflow-y-auto">
          <Dialog.Title asChild>
            <h2 className="sr-only">{lead.companyName}</h2>
          </Dialog.Title>

          {/* Editable company name. Acts as the visual title, commits
              on blur with the same trim-non-empty guard as the task
              slide-over title. */}
          <label className="block px-mx-5 pt-mx-4">
            <span className="mono-label text-mx-grey-mid">COMPANY</span>
            <input
              aria-label="Company name"
              value={companyDraft}
              onChange={(e) => setCompanyDraft(e.target.value)}
              onBlur={() => {
                const next = companyDraft.trim();
                if (next && next !== lead.companyName) {
                  updateLead.mutate({ id: leadId, patch: { companyName: next } });
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  e.currentTarget.blur();
                }
              }}
              className="mt-mx-1 block w-full border border-mx-grey-800 bg-mx-grey-900 px-mx-3 py-mx-2 font-display text-[20px] font-semibold leading-tight text-mx-grey-300 focus:border-mx-orange focus:outline-none"
            />
          </label>

          <dl className="grid grid-cols-[140px_1fr] items-center gap-y-mx-2 px-mx-5 py-mx-4 text-mx-body-sm">
            <dt className="mono-label text-mx-grey-mid">CONTACT NAME</dt>
            <dd>
              <input
                aria-label="Contact name"
                value={contactNameDraft}
                onChange={(e) => setContactNameDraft(e.target.value)}
                onBlur={() => commitNullableText('contactName', contactNameDraft)}
                className="h-9 w-full border border-mx-grey-800 bg-mx-grey-900 px-mx-3 text-mx-body-sm text-mx-grey-300 focus:border-mx-orange focus:outline-none"
              />
            </dd>

            <dt className="mono-label text-mx-grey-mid">CONTACT ROLE</dt>
            <dd>
              <input
                aria-label="Contact role"
                value={contactRoleDraft}
                onChange={(e) => setContactRoleDraft(e.target.value)}
                onBlur={() => commitNullableText('contactRole', contactRoleDraft)}
                className="h-9 w-full border border-mx-grey-800 bg-mx-grey-900 px-mx-3 text-mx-body-sm text-mx-grey-300 focus:border-mx-orange focus:outline-none"
              />
            </dd>

            <dt className="mono-label text-mx-grey-mid">CONTACT EMAIL</dt>
            <dd>
              <input
                type="email"
                aria-label="Contact email"
                value={contactEmailDraft}
                onChange={(e) => setContactEmailDraft(e.target.value)}
                onBlur={() => commitNullableText('contactEmail', contactEmailDraft)}
                className="h-9 w-full border border-mx-grey-800 bg-mx-grey-900 px-mx-3 text-mx-body-sm text-mx-grey-300 focus:border-mx-orange focus:outline-none"
              />
            </dd>

            <dt className="mono-label text-mx-grey-mid">CONTACT NUMBER</dt>
            <dd>
              <input
                type="tel"
                aria-label="Contact number"
                value={contactPhoneDraft}
                onChange={(e) => setContactPhoneDraft(e.target.value)}
                onBlur={() => commitNullableText('contactPhone', contactPhoneDraft)}
                className="h-9 w-full border border-mx-grey-800 bg-mx-grey-900 px-mx-3 text-mx-body-sm text-mx-grey-300 focus:border-mx-orange focus:outline-none"
              />
            </dd>

            <dt className="mono-label text-mx-grey-mid">INDUSTRY</dt>
            <dd>
              <select
                aria-label="Industry"
                value={lead.industry ?? ''}
                onChange={(e) =>
                  updateLead.mutate({
                    id: leadId,
                    patch: {
                      industry: e.target.value === '' ? null : (e.target.value as Industry),
                    },
                  })
                }
                className="h-9 w-full appearance-none border border-mx-grey-800 bg-mx-grey-900 px-mx-3 text-mx-body-sm text-mx-grey-300 focus:border-mx-orange focus:outline-none"
              >
                <option value="">—</option>
                {INDUSTRY.map((i) => (
                  <option key={i} value={i}>
                    {INDUSTRY_LABELS[i]}
                  </option>
                ))}
              </select>
            </dd>

            {/* Step 1: stage is derived from the Current Action, so it is
                read-only here — the Current Action select below drives it. */}
            <dt className="mono-label text-mx-grey-mid">STAGE</dt>
            <dd className="text-mx-grey-300">{lead.stage}</dd>

            <dt className="mono-label text-mx-grey-mid">SOURCE</dt>
            <dd>
              <select
                aria-label="Lead source"
                value={lead.source}
                onChange={(e) =>
                  updateLead.mutate({
                    id: leadId,
                    patch: { source: e.target.value as LeadSource },
                  })
                }
                className="h-9 w-full appearance-none border border-mx-grey-800 bg-mx-grey-900 px-mx-3 text-mx-body-sm text-mx-grey-300 focus:border-mx-orange focus:outline-none"
              >
                {LEAD_SOURCE.map((s) => (
                  <option key={s} value={s}>
                    {s.replace(/_/g, ' ')}
                  </option>
                ))}
              </select>
            </dd>

            <dt className="mono-label text-mx-grey-mid">OPP TYPE</dt>
            <dd>
              <select
                aria-label="Opportunity type"
                value={lead.oppType ?? ''}
                onChange={(e) =>
                  updateLead.mutate({
                    id: leadId,
                    patch: { oppType: e.target.value === '' ? null : (e.target.value as OppType) },
                  })
                }
                className="h-9 w-full appearance-none border border-mx-grey-800 bg-mx-grey-900 px-mx-3 text-mx-body-sm text-mx-grey-300 focus:border-mx-orange focus:outline-none"
              >
                <option value="">—</option>
                {OPP_TYPE.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </dd>

            <dt className="mono-label text-mx-grey-mid">CAPABILITY</dt>
            <dd>
              <select
                aria-label="Capability"
                value={lead.capability ?? ''}
                onChange={(e) =>
                  updateLead.mutate({
                    id: leadId,
                    patch: {
                      capability: e.target.value === '' ? null : (e.target.value as Capability),
                    },
                  })
                }
                className="h-9 w-full appearance-none border border-mx-grey-800 bg-mx-grey-900 px-mx-3 text-mx-body-sm text-mx-grey-300 focus:border-mx-orange focus:outline-none"
              >
                <option value="">—</option>
                {CAPABILITY.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </dd>

            <dt className="mono-label text-mx-grey-mid">CUSTOMER TYPE</dt>
            <dd>
              <select
                aria-label="Customer type"
                value={lead.customerType ?? ''}
                onChange={(e) =>
                  updateLead.mutate({
                    id: leadId,
                    patch: {
                      customerType: e.target.value === '' ? null : (e.target.value as CustomerType),
                    },
                  })
                }
                className="h-9 w-full appearance-none border border-mx-grey-800 bg-mx-grey-900 px-mx-3 text-mx-body-sm text-mx-grey-300 focus:border-mx-orange focus:outline-none"
              >
                <option value="">—</option>
                {CUSTOMER_TYPE.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </dd>

            <dt className="mono-label text-mx-grey-mid">CURRENT CUSTOMER</dt>
            <dd>
              <input
                type="checkbox"
                aria-label="Current customer"
                checked={lead.currentCustomer}
                onChange={(e) =>
                  updateLead.mutate({
                    id: leadId,
                    patch: { currentCustomer: e.target.checked },
                  })
                }
                className="h-4 w-4"
              />
            </dd>

            {/* Step 1: the user sets the Current Action; the Next Action and
                the stage derive from it. CONVERTED_TO_OPPORTUNITY is excluded
                (Convert button only); DISQUALIFIED routes the lead to DEAD and
                RECYCLE revives it — both handled server-side. */}
            <dt className="mono-label text-mx-grey-mid">CURRENT ACTION</dt>
            <dd>
              <select
                aria-label="Current action"
                value={lead.currentAction}
                onChange={(e) =>
                  updateLead.mutate({
                    id: leadId,
                    patch: { currentAction: e.target.value as LeadAction },
                  })
                }
                className="h-9 w-full appearance-none border border-mx-grey-800 bg-mx-grey-900 px-mx-3 text-mx-body-sm text-mx-grey-300 focus:border-mx-orange focus:outline-none"
              >
                {/* Keep the current value selectable even when it is a
                    non-dropdown state (e.g. NONE / CONVERTED_TO_OPPORTUNITY)
                    so the control renders the real value. */}
                {!LEAD_ACTION_SELECTABLE.includes(lead.currentAction as LeadAction) && (
                  <option value={lead.currentAction}>
                    {LEAD_ACTION_LABELS[lead.currentAction as LeadAction] ?? lead.currentAction}
                  </option>
                )}
                {LEAD_ACTION_SELECTABLE.map((a) => (
                  <option key={a} value={a}>
                    {LEAD_ACTION_LABELS[a]}
                  </option>
                ))}
              </select>
            </dd>

            <dt className="mono-label text-mx-grey-mid">NEXT ACTION</dt>
            <dd className="text-mx-grey-300">
              {(() => {
                const next = NEXT_LEAD_ACTION[lead.currentAction as LeadAction];
                return next ? LEAD_ACTION_LABELS[next] : '—';
              })()}
            </dd>

            <dt className="mono-label text-mx-grey-mid">POTENTIAL VALUE</dt>
            <dd>
              <label className="flex items-center gap-mx-1">
                <span aria-hidden className="mono-label text-mx-grey-mid">
                  $
                </span>
                <input
                  type="text"
                  inputMode="decimal"
                  aria-label="Potential value"
                  value={potentialValueDraft}
                  onChange={(e) => setPotentialValueDraft(e.target.value)}
                  onBlur={() => {
                    const trimmed = potentialValueDraft.trim();
                    if (trimmed === '') {
                      // potentialValue is non-nullable on Prisma but the
                      // wire shape always sends a string; treat empty as
                      // `0` to satisfy the schema.
                      if (lead.potentialValue !== '0') {
                        updateLead.mutate({ id: leadId, patch: { potentialValue: '0' } });
                      }
                      return;
                    }
                    const cleaned = trimmed.replace(/[^0-9.]/g, '');
                    const parsed = parseFloat(cleaned);
                    if (Number.isNaN(parsed)) return;
                    const next = parsed.toString();
                    if (next === lead.potentialValue) return;
                    updateLead.mutate({ id: leadId, patch: { potentialValue: next } });
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      e.currentTarget.blur();
                    }
                  }}
                  className="tnum h-9 w-full border border-mx-grey-800 bg-mx-grey-900 px-mx-2 text-mx-body-sm text-mx-grey-300 focus:border-mx-orange focus:outline-none"
                />
              </label>
            </dd>
          </dl>

          {/* Subtasks. UAT-28 added the lead variant: the
              `subtaskTemplates.newLead` list seeds four rows on lead
              create, and the slide-over surfaces them as a checklist
              with delete + new-row affordances. Mirror of the task
              slide-over surface that PR #25 shipped. */}
          <section className="border-t border-mx-grey-800 px-mx-5 py-mx-4">
            <p className="eyebrow mono-label text-mx-grey-mid">
              <span className="num">01</span>
              Subtasks
            </p>
            <ul className="mt-mx-2 flex flex-col gap-mx-1">
              {(lead.subtasks ?? []).map((sub) => (
                <li key={sub.id} className="flex items-center gap-mx-2">
                  <input
                    type="checkbox"
                    aria-label={`Subtask: ${sub.title}`}
                    checked={sub.done}
                    onChange={(e) =>
                      updateSubtask.mutate({
                        leadId,
                        subtaskId: sub.id,
                        patch: { done: e.target.checked },
                      })
                    }
                  />
                  <span
                    className={cn(
                      'flex-1 text-mx-body-sm',
                      sub.done ? 'text-mx-grey-mid line-through' : 'text-mx-grey-300',
                    )}
                  >
                    {sub.title}
                  </span>
                  <button
                    type="button"
                    aria-label={`Delete subtask ${sub.title}`}
                    onClick={() => deleteSubtask.mutate({ leadId, subtaskId: sub.id })}
                    className="text-mx-grey-mid hover:text-mx-neg"
                  >
                    <XMarkIcon className="h-4 w-4" strokeWidth={1.6} aria-hidden />
                  </button>
                </li>
              ))}
              {(lead.subtasks ?? []).length === 0 && (
                <li className="text-mx-body-sm text-mx-grey-mid">No subtasks yet.</li>
              )}
            </ul>
            <form
              className="mt-mx-3 flex gap-mx-2"
              onSubmit={(e) => {
                e.preventDefault();
                const next = newSubtaskTitle.trim();
                if (!next) return;
                createSubtask.mutate(
                  { leadId, input: { title: next } },
                  {
                    onSuccess: () => {
                      setNewSubtaskTitle('');
                    },
                  },
                );
              }}
            >
              <input
                aria-label="New lead subtask"
                value={newSubtaskTitle}
                onChange={(e) => setNewSubtaskTitle(e.target.value)}
                placeholder="Add subtask"
                className="h-8 flex-1 border border-mx-grey-800 bg-mx-grey-900 px-mx-2 text-mx-body-sm text-mx-grey-300 focus:border-mx-orange focus:outline-none"
              />
              <button
                type="submit"
                disabled={!newSubtaskTitle.trim() || createSubtask.isPending}
                className="h-8 border border-mx-grey-800 bg-mx-grey-900 px-mx-3 text-mx-mono-sm font-medium text-mx-grey-300 hover:bg-mx-grey-850 disabled:opacity-50"
              >
                ADD
              </button>
            </form>
          </section>

          {/* Notes textarea. Commits on blur. */}
          <section className="border-t border-mx-grey-800 px-mx-5 py-mx-4">
            <p className="eyebrow mono-label text-mx-grey-mid">
              <span className="num">02</span>
              Notes
            </p>
            <textarea
              aria-label="Lead notes"
              value={notesDraft}
              onChange={(e) => setNotesDraft(e.target.value)}
              onBlur={() => {
                const current = lead.notes ?? '';
                if (notesDraft !== current) {
                  updateLead.mutate({
                    id: leadId,
                    patch: { notes: notesDraft.length === 0 ? null : notesDraft },
                  });
                }
              }}
              rows={5}
              placeholder="Add notes."
              className="mt-mx-2 block w-full resize-y border border-mx-grey-800 bg-mx-grey-900 px-mx-3 py-mx-2 text-mx-body-sm text-mx-grey-300 focus:border-mx-orange focus:outline-none"
            />
          </section>

          {lead.stage !== 'CONVERTED' && lead.stage !== 'DEAD' && (
            <section className="border-t border-mx-grey-800 bg-mx-grey-900 px-mx-5 py-mx-4">
              <p className="mono-label text-mx-grey-mid">CONVERT TO OPPORTUNITY</p>
              <div className="mt-mx-2 flex gap-mx-2">
                <input
                  value={opportunityName}
                  onChange={(e) => setOpportunityName(e.target.value)}
                  placeholder="Opportunity name"
                  className="h-9 flex-1 border border-mx-grey-800 bg-transparent px-mx-3 text-mx-body-sm text-mx-grey-300 focus:border-mx-orange focus:outline-none"
                />
                <button
                  type="button"
                  disabled={!opportunityName.trim() || convert.isPending}
                  onClick={() =>
                    convert.mutate(
                      { id: leadId, body: { opportunityName: opportunityName.trim() } },
                      { onSuccess: close },
                    )
                  }
                  className="h-9 bg-mx-orange px-mx-3 text-mx-body-sm font-medium text-mx-white hover:bg-mx-orange-hover disabled:bg-mx-grey-mid"
                >
                  Convert
                </button>
              </div>
            </section>
          )}

          <AuditLog entries={lead.auditLog ?? []} eyebrowNum="03" />

          <footer className="mt-auto flex items-center gap-mx-2 border-t border-mx-grey-800 bg-mx-grey-900 px-mx-5 py-mx-4">
            {/* PR #39 (UAT-39): destructive delete sits first. The
                backend soft-deletes (sets DEAD + stamps
                `disqualifiedAt`) so a hand-pulled row is recoverable
                via the same recycle path as a manually disqualified
                one. No confirm modal because the slide-over closing
                is itself reversible signal. Visible on every stage so
                Peter can remove imported junk regardless of where it
                lives on the board. */}
            <button
              type="button"
              onClick={() => deleteLead.mutate(leadId, { onSuccess: close })}
              className="text-mx-mono-sm font-medium text-mx-neg hover:opacity-80"
            >
              DELETE
            </button>
            {lead.stage !== 'DEAD' && (
              <button
                type="button"
                onClick={() => disqualify.mutate({ id: leadId }, { onSuccess: close })}
                className="text-mx-mono text-mx-grey-mid hover:text-mx-white"
              >
                DISQUALIFY
              </button>
            )}
            {/* PR #34 (UAT-34): recycle a DEAD lead back to TARGET. The
                recycle endpoint clears `disqualifiedAt`, resets the
                planned next action to PLAN_OUTREACH, and seeds the due
                date to today. The select-driven path above does the
                same when Peter changes the stage select away from
                DEAD; this button is the explicit affordance. */}
            {lead.stage === 'DEAD' && (
              <button
                type="button"
                onClick={() => recycle.mutate(leadId, { onSuccess: close })}
                className="text-mx-mono text-mx-orange hover:text-mx-orange-hover"
              >
                RECYCLE
              </button>
            )}
          </footer>
        </div>
      )}
    </>
  );
}

// The wire shape on `WireOpp` types these as `string` (Prisma decimals
// serialise that way), so we use a concrete tuple union here and cast
// at the select-change boundary.
type WinProbabilityEnum = 'COLD' | 'MAYBE' | 'LIKELY' | 'STRONG' | 'LOCKED';

const PROBABILITIES: WinProbabilityEnum[] = ['COLD', 'MAYBE', 'LIKELY', 'STRONG', 'LOCKED'];

/**
 * Opportunity slide-over.
 *
 * UAT-34 (PR #34) extends PR #30's budget-only inline edit into a full
 * editor: every wire field becomes a controlled input or select that
 * PATCHes on blur (text / textarea / number / date) or change (selects).
 * Stage transitions to WON / LOST keep their dedicated endpoints
 * (`/win`, `/lose`) so the celebration overlay fires; other stage
 * moves go through the plain PATCH path. The currentStep select still
 * routes through `useAdvanceOpportunityStep` so the section-5.7
 * cascade applies.
 */
function OpportunitySlideOver({ opportunityId }: { opportunityId: string }) {
  const close = useSlideOver((s) => s.close);
  const { data: opp, isLoading, isError } = useOpportunity(opportunityId);
  const updateOpportunity = useUpdateOpportunity();
  const winOpportunity = useWinOpportunity();
  const loseOpportunity = useLoseOpportunity();
  const deleteOpportunity = useDeleteOpportunity();
  const createSubtask = useCreateOpportunitySubtask();
  const updateSubtask = useUpdateOpportunitySubtask();
  const deleteSubtask = useDeleteOpportunitySubtask();
  const [newSubtaskTitle, setNewSubtaskTitle] = useState('');

  // Local controlled-input drafts. Seeded from the wire payload on row
  // identity / updatedAt change so a save round-trip does not clobber
  // an in-progress edit (mirrors the TaskSlideOver pattern).
  const [companyDraft, setCompanyDraft] = useState('');
  const [oppNameDraft, setOppNameDraft] = useState('');
  const [notesDraft, setNotesDraft] = useState('');
  const [budgetDraft, setBudgetDraft] = useState('');

  useEffect(() => {
    if (opp) {
      setCompanyDraft(opp.companyName);
      setOppNameDraft(opp.opportunityName);
      setNotesDraft(opp.notes ?? '');
      setBudgetDraft(opp.budgetFee ?? '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opp?.id, opp?.updatedAt]);

  /**
   * Commit the in-progress budget edit. Strips formatting characters
   * before parsing so users can type `$120,000` and still send a valid
   * decimal string. An empty input clears the value (`null`).
   */
  function commitBudget(): void {
    if (!opp) return;
    const trimmed = budgetDraft.trim();
    if (trimmed === '') {
      if (opp.budgetFee !== null) {
        updateOpportunity.mutate({ id: opportunityId, patch: { budgetFee: null } });
      }
      return;
    }
    const cleaned = trimmed.replace(/[^0-9.]/g, '');
    const parsed = parseFloat(cleaned);
    if (Number.isNaN(parsed)) return;
    const next = parsed.toString();
    if (next === opp.budgetFee) return;
    updateOpportunity.mutate({ id: opportunityId, patch: { budgetFee: next } });
  }

  /**
   * Stage-select handler. WON / LOST keep their dedicated endpoints so
   * the celebration overlay fires; all other transitions take the plain
   * PATCH path. Bailing out when the value is unchanged guards against
   * spurious change events.
   */
  const stageLocked = opp ? opp.stage === 'WON' || opp.stage === 'LOST' : false;

  return (
    <>
      <header className="flex items-center justify-between border-b border-mx-grey-800 px-mx-5 py-mx-4">
        <div className="flex items-center gap-mx-2">
          {opp && (
            <>
              <span className="mono-label border border-mx-grey-800 bg-mx-grey-900 px-mx-2 py-[2px] text-mx-mono-sm">
                {opp.oppType}
              </span>
              <span className="mono-label border border-mx-grey-800 bg-mx-grey-900 px-mx-2 py-[2px] text-mx-mono-sm">
                {opp.stage}
              </span>
              <span className="mono-label border border-mx-grey-800 bg-mx-grey-900 px-mx-2 py-[2px] text-mx-mono-sm">
                {opp.winProbability}
              </span>
            </>
          )}
        </div>
        <Dialog.Close
          aria-label="Close detail panel"
          className="flex h-7 w-7 items-center justify-center text-mx-grey-mid hover:text-mx-white"
        >
          <XMarkIcon className="h-5 w-5" strokeWidth={1.6} aria-hidden />
        </Dialog.Close>
      </header>

      <Dialog.Description className="sr-only">Opportunity detail panel.</Dialog.Description>

      {isLoading && <p className="px-mx-5 py-mx-5 text-mx-body-sm text-mx-grey-mid">Loading…</p>}
      {isError && (
        <p className="px-mx-5 py-mx-5 text-mx-body-sm text-mx-neg">Could not load opportunity.</p>
      )}

      {opp && (
        <div className="flex flex-1 flex-col overflow-y-auto">
          <Dialog.Title asChild>
            <h2 className="sr-only">{opp.opportunityName}</h2>
          </Dialog.Title>

          {/* Editable opportunity name. Commits on blur with the same
              non-empty guard as the task title. */}
          <label className="block px-mx-5 pt-mx-4">
            <span className="mono-label text-mx-grey-mid">OPPORTUNITY NAME</span>
            <input
              aria-label="Opportunity name"
              value={oppNameDraft}
              onChange={(e) => setOppNameDraft(e.target.value)}
              onBlur={() => {
                const next = oppNameDraft.trim();
                if (next && next !== opp.opportunityName) {
                  updateOpportunity.mutate({
                    id: opportunityId,
                    patch: { opportunityName: next },
                  });
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  e.currentTarget.blur();
                }
              }}
              className="mt-mx-1 block w-full border border-mx-grey-800 bg-mx-grey-900 px-mx-3 py-mx-2 font-display text-[20px] font-semibold leading-tight text-mx-grey-300 focus:border-mx-orange focus:outline-none"
            />
          </label>

          <dl className="grid grid-cols-[140px_1fr] items-center gap-y-mx-2 px-mx-5 py-mx-4 text-mx-body-sm">
            <dt className="mono-label text-mx-grey-mid">COMPANY</dt>
            <dd>
              <input
                aria-label="Company name"
                value={companyDraft}
                onChange={(e) => setCompanyDraft(e.target.value)}
                onBlur={() => {
                  const next = companyDraft.trim();
                  if (next && next !== opp.companyName) {
                    updateOpportunity.mutate({
                      id: opportunityId,
                      patch: { companyName: next },
                    });
                  }
                }}
                className="h-9 w-full border border-mx-grey-800 bg-mx-grey-900 px-mx-3 text-mx-body-sm text-mx-grey-300 focus:border-mx-orange focus:outline-none"
              />
            </dd>

            {/* Step 1: contact carried over from the originating lead. */}
            <dt className="mono-label text-mx-grey-mid">CONTACT</dt>
            <dd className="text-mx-grey-300">{opp.contactName ?? '—'}</dd>

            <dt className="mono-label text-mx-grey-mid">EMAIL</dt>
            <dd className="text-mx-grey-300">{opp.contactEmail ?? '—'}</dd>

            <dt className="mono-label text-mx-grey-mid">PHONE</dt>
            <dd className="text-mx-grey-300">{opp.contactPhone ?? '—'}</dd>

            {/* Step 1: stage is derived from the Current Action and shown
                read-only. WON / LOST are reached via the Mark Won / Mark Lost
                buttons in the footer (not this control). */}
            <dt className="mono-label text-mx-grey-mid">STAGE</dt>
            <dd className="text-mx-grey-300">{opp.stage}</dd>

            <dt className="mono-label text-mx-grey-mid">CURRENT ACTION</dt>
            <dd>
              <select
                aria-label="Current action"
                value={opp.currentAction}
                disabled={stageLocked}
                onChange={(e) =>
                  updateOpportunity.mutate({
                    id: opportunityId,
                    patch: { currentAction: e.target.value as OpportunityAction },
                  })
                }
                className="h-9 w-full appearance-none border border-mx-grey-800 bg-mx-grey-900 px-mx-3 text-mx-body-sm text-mx-grey-300 focus:border-mx-orange focus:outline-none disabled:opacity-50"
              >
                {!OPPORTUNITY_ACTION_SELECTABLE.includes(
                  opp.currentAction as OpportunityAction,
                ) && (
                  <option value={opp.currentAction}>
                    {OPPORTUNITY_ACTION_LABELS[opp.currentAction as OpportunityAction] ??
                      opp.currentAction}
                  </option>
                )}
                {OPPORTUNITY_ACTION_SELECTABLE.map((a) => (
                  <option key={a} value={a}>
                    {OPPORTUNITY_ACTION_LABELS[a]}
                  </option>
                ))}
              </select>
            </dd>

            <dt className="mono-label text-mx-grey-mid">NEXT ACTION</dt>
            <dd className="text-mx-grey-300">
              {(() => {
                const next = NEXT_OPPORTUNITY_ACTION[opp.currentAction as OpportunityAction];
                return next ? OPPORTUNITY_ACTION_LABELS[next] : '—';
              })()}
            </dd>

            <dt className="mono-label text-mx-grey-mid">PROBABILITY</dt>
            <dd>
              <select
                aria-label="Win probability"
                value={opp.winProbability}
                disabled={stageLocked}
                onChange={(e) =>
                  updateOpportunity.mutate({
                    id: opportunityId,
                    patch: { winProbability: e.target.value as WinProbabilityEnum },
                  })
                }
                className="h-9 w-full appearance-none border border-mx-grey-800 bg-mx-grey-900 px-mx-3 text-mx-body-sm text-mx-grey-300 focus:border-mx-orange focus:outline-none disabled:opacity-50"
              >
                {PROBABILITIES.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </dd>

            <dt className="mono-label text-mx-grey-mid">OPP TYPE</dt>
            <dd>
              <select
                aria-label="Opportunity type"
                value={opp.oppType}
                onChange={(e) =>
                  updateOpportunity.mutate({
                    id: opportunityId,
                    patch: { oppType: e.target.value as OppType },
                  })
                }
                className="h-9 w-full appearance-none border border-mx-grey-800 bg-mx-grey-900 px-mx-3 text-mx-body-sm text-mx-grey-300 focus:border-mx-orange focus:outline-none"
              >
                {OPP_TYPE.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </dd>

            <dt className="mono-label text-mx-grey-mid">CAPABILITY</dt>
            <dd>
              <select
                aria-label="Capability"
                value={opp.capability ?? ''}
                onChange={(e) =>
                  updateOpportunity.mutate({
                    id: opportunityId,
                    patch: {
                      capability: e.target.value === '' ? null : (e.target.value as Capability),
                    },
                  })
                }
                className="h-9 w-full appearance-none border border-mx-grey-800 bg-mx-grey-900 px-mx-3 text-mx-body-sm text-mx-grey-300 focus:border-mx-orange focus:outline-none"
              >
                <option value="">—</option>
                {CAPABILITY.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </dd>

            <dt className="mono-label text-mx-grey-mid">BUDGET</dt>
            <dd>
              <label className="flex items-center gap-mx-1">
                <span aria-hidden className="mono-label text-mx-grey-mid">
                  $
                </span>
                <input
                  type="text"
                  inputMode="decimal"
                  aria-label="Opportunity budget"
                  value={budgetDraft}
                  onChange={(e) => setBudgetDraft(e.target.value)}
                  onBlur={commitBudget}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      e.currentTarget.blur();
                    }
                  }}
                  className="tnum h-9 w-full border border-mx-grey-800 bg-mx-grey-900 px-mx-2 text-mx-body-sm text-mx-grey-300 focus:border-mx-orange focus:outline-none"
                />
              </label>
            </dd>

            <dt className="mono-label text-mx-grey-mid">SIGNAL</dt>
            <dd>
              <select
                aria-label="Budget signal"
                value={opp.budgetSignal}
                onChange={(e) =>
                  updateOpportunity.mutate({
                    id: opportunityId,
                    patch: { budgetSignal: e.target.value as BudgetSignal },
                  })
                }
                className="h-9 w-full appearance-none border border-mx-grey-800 bg-mx-grey-900 px-mx-3 text-mx-body-sm text-mx-grey-300 focus:border-mx-orange focus:outline-none"
              >
                {BUDGET_SIGNAL.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </dd>

            <dt className="mono-label text-mx-grey-mid">CUSTOMER TYPE</dt>
            <dd>
              <select
                aria-label="Customer type"
                value={opp.customerType ?? ''}
                onChange={(e) =>
                  updateOpportunity.mutate({
                    id: opportunityId,
                    patch: {
                      customerType: e.target.value === '' ? null : (e.target.value as CustomerType),
                    },
                  })
                }
                className="h-9 w-full appearance-none border border-mx-grey-800 bg-mx-grey-900 px-mx-3 text-mx-body-sm text-mx-grey-300 focus:border-mx-orange focus:outline-none"
              >
                <option value="">—</option>
                {CUSTOMER_TYPE.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </dd>

            <dt className="mono-label text-mx-grey-mid">CUSTOMER STATUS</dt>
            <dd>
              <select
                aria-label="Customer status"
                value={opp.customerStatus}
                onChange={(e) =>
                  updateOpportunity.mutate({
                    id: opportunityId,
                    patch: { customerStatus: e.target.value as CustomerStatus },
                  })
                }
                className="h-9 w-full appearance-none border border-mx-grey-800 bg-mx-grey-900 px-mx-3 text-mx-body-sm text-mx-grey-300 focus:border-mx-orange focus:outline-none"
              >
                {CUSTOMER_STATUS.map((s) => (
                  <option key={s} value={s}>
                    {s.replace(/_/g, ' ')}
                  </option>
                ))}
              </select>
            </dd>

            <dt className="mono-label text-mx-grey-mid">SOURCE</dt>
            <dd>
              <select
                aria-label="Opportunity source"
                value={opp.source}
                onChange={(e) =>
                  updateOpportunity.mutate({
                    id: opportunityId,
                    patch: { source: e.target.value as LeadSource },
                  })
                }
                className="h-9 w-full appearance-none border border-mx-grey-800 bg-mx-grey-900 px-mx-3 text-mx-body-sm text-mx-grey-300 focus:border-mx-orange focus:outline-none"
              >
                {LEAD_SOURCE.map((s) => (
                  <option key={s} value={s}>
                    {s.replace(/_/g, ' ')}
                  </option>
                ))}
              </select>
            </dd>
          </dl>

          {/* Subtasks. UAT-28 added the opportunity variant: the
              `subtaskTemplates.newOpp` list seeds rows on lead-convert
              and on manual opportunity creation; `subtaskTemplates.atProposal`
              appends when the step advances into DRAFT_PROPOSAL. Same
              checklist UX as the task and lead variants. */}
          <section className="border-t border-mx-grey-800 px-mx-5 py-mx-4">
            <p className="eyebrow mono-label text-mx-grey-mid">
              <span className="num">01</span>
              Subtasks
            </p>
            <ul className="mt-mx-2 flex flex-col gap-mx-1">
              {(opp.subtasks ?? []).map((sub) => (
                <li key={sub.id} className="flex items-center gap-mx-2">
                  <input
                    type="checkbox"
                    aria-label={`Subtask: ${sub.title}`}
                    checked={sub.done}
                    onChange={(e) =>
                      updateSubtask.mutate({
                        opportunityId,
                        subtaskId: sub.id,
                        patch: { done: e.target.checked },
                      })
                    }
                  />
                  <span
                    className={cn(
                      'flex-1 text-mx-body-sm',
                      sub.done ? 'text-mx-grey-mid line-through' : 'text-mx-grey-300',
                    )}
                  >
                    {sub.title}
                  </span>
                  <button
                    type="button"
                    aria-label={`Delete subtask ${sub.title}`}
                    onClick={() => deleteSubtask.mutate({ opportunityId, subtaskId: sub.id })}
                    className="text-mx-grey-mid hover:text-mx-neg"
                  >
                    <XMarkIcon className="h-4 w-4" strokeWidth={1.6} aria-hidden />
                  </button>
                </li>
              ))}
              {(opp.subtasks ?? []).length === 0 && (
                <li className="text-mx-body-sm text-mx-grey-mid">No subtasks yet.</li>
              )}
            </ul>
            <form
              className="mt-mx-3 flex gap-mx-2"
              onSubmit={(e) => {
                e.preventDefault();
                const next = newSubtaskTitle.trim();
                if (!next) return;
                createSubtask.mutate(
                  { opportunityId, input: { title: next } },
                  {
                    onSuccess: () => {
                      setNewSubtaskTitle('');
                    },
                  },
                );
              }}
            >
              <input
                aria-label="New opportunity subtask"
                value={newSubtaskTitle}
                onChange={(e) => setNewSubtaskTitle(e.target.value)}
                placeholder="Add subtask"
                className="h-8 flex-1 border border-mx-grey-800 bg-mx-grey-900 px-mx-2 text-mx-body-sm text-mx-grey-300 focus:border-mx-orange focus:outline-none"
              />
              <button
                type="submit"
                disabled={!newSubtaskTitle.trim() || createSubtask.isPending}
                className="h-8 border border-mx-grey-800 bg-mx-grey-900 px-mx-3 text-mx-mono-sm font-medium text-mx-grey-300 hover:bg-mx-grey-850 disabled:opacity-50"
              >
                ADD
              </button>
            </form>
          </section>

          {/* Notes textarea. Commits on blur. */}
          <section className="border-t border-mx-grey-800 px-mx-5 py-mx-4">
            <p className="eyebrow mono-label text-mx-grey-mid">
              <span className="num">02</span>
              Notes
            </p>
            <textarea
              aria-label="Opportunity notes"
              value={notesDraft}
              onChange={(e) => setNotesDraft(e.target.value)}
              onBlur={() => {
                const current = opp.notes ?? '';
                if (notesDraft !== current) {
                  updateOpportunity.mutate({
                    id: opportunityId,
                    patch: { notes: notesDraft.length === 0 ? null : notesDraft },
                  });
                }
              }}
              rows={5}
              placeholder="Add notes."
              className="mt-mx-2 block w-full resize-y border border-mx-grey-800 bg-mx-grey-900 px-mx-3 py-mx-2 text-mx-body-sm text-mx-grey-300 focus:border-mx-orange focus:outline-none"
            />
          </section>

          <AuditLog entries={opp.auditLog ?? []} eyebrowNum="03" />

          <footer className="mt-auto flex items-center gap-mx-2 border-t border-mx-grey-800 bg-mx-grey-900 px-mx-5 py-mx-4">
            {/* PR #39 (UAT-39): destructive delete sits first. The
                backend soft-deletes (stamps `disqualifiedAt` and
                writes a DISQUALIFIED audit row) so a hand-pulled row
                is recoverable from the audit log. No confirm modal
                because the slide-over close is itself the
                reversible signal. Visible on every stage so Peter can
                clear out imported junk from any column. */}
            <button
              type="button"
              onClick={() => deleteOpportunity.mutate(opportunityId, { onSuccess: close })}
              className="text-mx-mono-sm font-medium text-mx-neg hover:opacity-80"
            >
              DELETE
            </button>
            {opp.stage !== 'LOST' && opp.stage !== 'WON' && (
              <button
                type="button"
                onClick={() =>
                  loseOpportunity.mutate(opportunityId, {
                    onSuccess: () => {
                      // UAT 7.7-7.9: fire the shell-level overlay so the
                      // user sees confirmation even though the slide-over
                      // closes. LOST uses the quieter neutral framing.
                      useCelebration.getState().show(opp.opportunityName, 'LOST');
                      close();
                    },
                  })
                }
                className="text-mx-mono text-mx-grey-mid hover:text-mx-white"
              >
                MARK LOST
              </button>
            )}
            {opp.stage !== 'WON' && opp.stage !== 'LOST' && (
              <button
                type="button"
                onClick={() =>
                  winOpportunity.mutate(opportunityId, {
                    onSuccess: () => {
                      // UAT 7.7-7.9: fire the shell-level celebration so
                      // clicking Mark won surfaces the same overlay as
                      // dragging onto the WON column. The slide-over
                      // still closes after the chip is queued.
                      useCelebration.getState().show(opp.opportunityName, 'WON');
                      close();
                    },
                  })
                }
                className="ml-auto flex h-9 items-center bg-mx-pos px-mx-3 text-mx-body-sm font-medium text-mx-white hover:opacity-90"
              >
                Mark won
              </button>
            )}
          </footer>
        </div>
      )}
    </>
  );
}
